import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  IconButton,
  Button,
  AppBar,
  Toolbar,
  Switch,
  FormControlLabel,
  Container,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../../contexts/langContext';
import { useTags } from '../../hooks/useTags';
import type { Tag, MaterialWithFileMetadata } from '../../types';

interface MaterialDetailProps {
  material: MaterialWithFileMetadata;
  projectId?: string;
  onDelete?: (materialId: string) => Promise<void>;
  onToggleEnabled?: (materialId: string) => Promise<void>;
}

function MaterialDetail({ material, projectId, onDelete, onToggleEnabled }: MaterialDetailProps) {
  const navigate = useNavigate();
  const { lang } = useLang();
  const { tags } = useTags();
  const tagMap = useMemo(() => {
    const map: Record<string, Tag> = {};
    tags.forEach(t => { map[t._id] = t; });
    return map;
  }, [tags]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  const handleBack = () => {
    navigate(projectId ? `/projects/${projectId}/materials` : '/materials');
  };

  const handleDelete = async () => {
    if (!onDelete || isDeleting) return;
    setIsDeleting(true);
    try {
      await onDelete(material._id);
      handleBack();
    } catch (error) {
      console.error('Error deleting material:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleEnabled = async () => {
    if (!onToggleEnabled || isToggling) return;
    setIsToggling(true);
    try {
      await onToggleEnabled(material._id);
    } catch (error) {
      console.error('Error toggling material:', error);
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <Container>
      <AppBar position="sticky" color="default" elevation={0}>
        <Toolbar>
          <IconButton edge="start" onClick={handleBack} aria-label="back">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, ml: 2 }}>
            <Typography variant="h6" noWrap>
              {material.fileMetadata?.filename || 'Untitled'}
            </Typography>
          </Box>
          {onToggleEnabled && (
            <FormControlLabel
              control={
                <Switch
                  checked={material.enabled}
                  onChange={handleToggleEnabled}
                  disabled={isToggling}
                />
              }
              label={lang === 'en' ? 'Enabled' : '启用'}
            />
          )}
          {onDelete && (
            <IconButton 
              color="error" 
              onClick={handleDelete}
              disabled={isDeleting}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  {lang === 'en' ? 'File Information' : '文件信息'}
                </Typography>
                <Typography variant="body2">
                  {lang === 'en' ? 'Type: ' : '类型：'}
                  {material.fileMetadata?.type || (lang === 'en' ? 'Unknown' : '未知')}
                </Typography>
                <Typography variant="body2">
                  {lang === 'en' ? 'Created: ' : '创建时间：'}
                  {new Date(material.createdAt).toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  {lang === 'en' ? 'File Status: ' : '文件处理状态：'}
                  {material.fileMetadata?.status === 'completed' ? (lang === 'en' ? 'Completed' : '已完成') : (lang === 'en' ? 'Processing' : '处理中')}
                </Typography>
              </Box>

              {(material.tagIds || []).length > 0 && (
                <Box>
                  <Typography variant="overline" color="text.secondary" gutterBottom>
                    {lang === 'en' ? 'Tags' : '标签'}
                  </Typography>
                  <Box>
                    {(material.tagIds || []).map((tagId: string) => {
                      const tagName = tagMap[tagId]?.name || tagId;
                      return (
                        <Chip 
                          key={tagId} 
                          label={tagName} 
                          size="small" 
                          sx={{ mr: 0.5, mb: 0.5, maxWidth: 160, '.MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} 
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}

              {material.fileMetadata?.parsedText && (
                <Box>
                  <Typography variant="overline" color="text.secondary" gutterBottom>
                    {lang === 'en' ? 'Content' : '内容'}
                  </Typography>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'monospace',
                        }}
                      >
                        {material.fileMetadata.parsedText}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}

export default MaterialDetail; 